import tinycolor from 'tinycolor2';

import type { SectionBackgroundContentTone } from '@/lib/section-background';
import { getDocument } from '@/lib/utils';
import type { ColorSettings } from '@/types/shared';
import { DESIGN_COLOR_VARIABLE_MAP } from './site-design/theme';

const CSS_VAR_TO_COLOR_KEY: Record<string, keyof ColorSettings> = {};
for (const [key, vars] of Object.entries(DESIGN_COLOR_VARIABLE_MAP)) {
  for (const v of Array.isArray(vars) ? vars : [vars]) {
    CSS_VAR_TO_COLOR_KEY[v] = key as keyof ColorSettings;
  }
}

export function resolveColorVariable(color: string, colors: Partial<ColorSettings>): string | null {
  const match = /^var\((--[\w-]+)\)$/.exec(color);
  if (!match) return null;

  const key = CSS_VAR_TO_COLOR_KEY[match[1]];
  return key ? colors[key] || null : null;
}

export function resolveCssVariable(color: string): string {
  const match = /^var\((--[\w-]+)\)$/.exec(color);
  if (!match) return color;

  const doc = getDocument();
  if (!doc) return color;

  const themeEl =
    doc.querySelector<HTMLElement>('[data-theme-root]') ??
    doc.querySelector<HTMLElement>('.public-theme') ??
    doc.documentElement;

  const resolved = getComputedStyle(themeEl).getPropertyValue(match[1]).trim();
  return resolved || color;
}

const LIGHT_TONE_TEXT_COLOR = '#ffffff';
const DARK_TONE_TEXT_COLOR = '#000000';

// Prefers the site's own text color when it's WCAG-readable against the background (brand
// consistency), falling back to whichever of true black/white contrasts better otherwise.
// When the site's text color isn't known at all, skip straight to the black/white fallback —
// a real (if imperfect) answer beats guessing at an unresolved CSS variable.
function pickReadableTone(
  backgroundColor: string,
  siteTextColor?: string,
): SectionBackgroundContentTone {
  if (siteTextColor && tinycolor.isReadable(backgroundColor, siteTextColor)) return 'siteText';

  const fallback = tinycolor.mostReadable(
    backgroundColor,
    [LIGHT_TONE_TEXT_COLOR, DARK_TONE_TEXT_COLOR],
    { includeFallbackColors: false },
  );

  return fallback.toHexString() === tinycolor(LIGHT_TONE_TEXT_COLOR).toHexString()
    ? 'light'
    : 'dark';
}

export function deriveContentToneFromColor(
  color: string,
  siteTextColor?: string,
): SectionBackgroundContentTone | null {
  const resolved = resolveCssVariable(color);
  const tc = tinycolor(resolved);

  if (!tc.isValid()) return null;

  return pickReadableTone(
    tc.toHexString(),
    siteTextColor ? resolveCssVariable(siteTextColor) : undefined,
  );
}

// Image/video backgrounds always render behind a color overlay (semi-transparent black at 50%
// by default). We can't know the image's real pixel colors without sampling, but we do know the
// overlay color/opacity, so we score tone against the overlay mixed over a neutral mid-gray — a
// conservative stand-in for "an image whose brightness we can't see".
const UNKNOWN_IMAGE_BASE_COLOR = '#808080';

export function deriveContentToneFromOverlay(
  overlayColor: string,
  overlayOpacityPercent: number,
  siteTextColor?: string,
): SectionBackgroundContentTone {
  const effectiveColor = tinycolor.mix(
    UNKNOWN_IMAGE_BASE_COLOR,
    overlayColor,
    overlayOpacityPercent,
  );

  return pickReadableTone(
    effectiveColor.toHexString(),
    siteTextColor ? resolveCssVariable(siteTextColor) : undefined,
  );
}

export const SECTION_BACKGROUND_CONTENT_TONE_CLASSNAMES: Record<
  SectionBackgroundContentTone,
  string
> = {
  light: 'text-white [&_.prose]:prose-invert [&_.prose]:text-white',
  dark: 'text-black [&_.prose]:text-black [&_.prose_*]:text-inherit',
  siteText: 'text-foreground [&_.prose]:text-foreground [&_.prose_*]:text-inherit',
};
